import { faEye, faEyeSlash, faKey, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from "../../../hooks/useAuth";

const PasswordField = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  autoComplete,
  visible,
  onToggle
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  visible: boolean;
  onToggle: () => void;
}) => (
  <Form.Group className="mb-3 text-start">
    <Form.Label htmlFor={id}>{label}</Form.Label>
    <div className="form-icon-container">
      <Form.Control
        id={id}
        type={visible ? 'text' : 'password'}
        className="form-icon-input"
        style={{ paddingRight: '2.5rem' }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
      />
      <FontAwesomeIcon icon={faKey} className="text-900 fs-9 form-icon" />
      <button
        type="button"
        className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-700 px-3 py-0 border-0 shadow-none"
        onClick={onToggle}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
      >
        <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
      </button>
    </div>
  </Form.Group>
);

const SignInForm = ({
  showAccountLinks = true,
  title = "Welcome Back",
  description = "Sign in to your carrier data foundation",
}: {
  layout: 'simple' | 'card' | 'split';
  showAccountLinks?: boolean;
  title?: string;
  description?: string;
}) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showNewPasswordStep, setShowNewPasswordStep] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const auth = useAuth();
  const navigate = useNavigate();

  const executeSignIn = async () => {
    setErrorMessage("");
    const result = await auth.handleSignIn(username, password);
    if (result.success) {
      navigate({ pathname: "/" });
    } else if (result.message === "NEW_PASSWORD_REQUIRED") {
      setShowNewPasswordStep(true);
      setErrorMessage("");
    } else {
      setErrorMessage(result.message);
    }
  };

  const executeSetNewPassword = async () => {
    setErrorMessage("");
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    const result = await auth.handleConfirmNewPassword(newPassword);
    if (result.success) {
      navigate({ pathname: "/" });
    } else {
      setErrorMessage(result.message);
    }
  };

  if (showNewPasswordStep) {
    return (
      <>
        <div className="text-center mb-7">
          <h3 className="text-1000">Set new password</h3>
          <p className="text-700">Your account requires a new password. Choose a password you’ll remember.</p>
        </div>
        <PasswordField
          id="new-password"
          label="New password"
          placeholder="New password"
          value={newPassword}
          onChange={setNewPassword}
          autoComplete="new-password"
          visible={showNewPassword}
          onToggle={() => setShowNewPassword((current) => !current)}
        />
        <PasswordField
          id="confirm-new-password"
          label="Confirm new password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          autoComplete="new-password"
          visible={showConfirmPassword}
          onToggle={() => setShowConfirmPassword((current) => !current)}
        />
        {errorMessage && <div className="text-danger mb-3">{errorMessage}</div>}
        <Button variant="primary" className="w-100 mb-3" onClick={executeSetNewPassword}>
          Set password
        </Button>
        <button
          type="button"
          className="btn btn-link w-100 text-700"
          onClick={() => { setShowNewPasswordStep(false); setErrorMessage(""); setNewPassword(""); setConfirmPassword(""); }}
        >
          Back to sign in
        </button>
      </>
    );
  }

  return (
    <>
        <div className="text-center mb-7">
          <h3 className="text-1000">{title}</h3>
          <p className="text-700">{description}</p>
        </div>
      <Form.Group className="mb-3 text-start">
        <Form.Label htmlFor="email">Email address</Form.Label>
        <div className="form-icon-container">
          <Form.Control
            id="email"
            type="email"
            className="form-icon-input"
            placeholder="name@example.com"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <FontAwesomeIcon icon={faUser} className="text-900 fs-9 form-icon" />
        </div>
      </Form.Group>

      <PasswordField
        id="password"
        label="Password"
        placeholder="Password"
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
        visible={showPassword}
        onToggle={() => setShowPassword((current) => !current)}
      />

      <Row className="flex-between-center mb-7">
        <Col xs="auto">
          <Form.Check type="checkbox" className="mb-0">
            <Form.Check.Input
              type="checkbox"
              name="remember-me"
              id="remember-me"
              defaultChecked
            />
            <Form.Check.Label htmlFor="remember-me" className="mb-0">
              Remember me
            </Form.Check.Label>
          </Form.Check>
        </Col>
        {showAccountLinks && (
          <Col xs="auto">
            <Link to="../forgot-password" className="fs-9 fw-semi-bold">
              Forgot Password?
            </Link>
          </Col>
        )}
      </Row>

      {errorMessage && <div className="text-danger mb-3">{errorMessage}</div>}

      <Button variant="primary" className="w-100 mb-3" onClick={() => executeSignIn()}>
        Sign In
      </Button>
      {showAccountLinks && (
        <div className="text-center">
          <Link to="../sign-up" className="fs-9 fw-bold">
            Create an account
          </Link>
        </div>
      )}
    </>
  );
};

export default SignInForm;
